import React, { useRef, useState } from 'react';
import type {
  Lfa_Conversion_Location_Type,
  LfaServiceDetailsFieldsFragment,
  LfaServiceLocationFieldsFragment,
} from '~/graphql/types';
import type { UpdateSettingsAction } from '../../../../reducers/settingsReducer';
import JustificationContainer from '~/components/atom/JustificationContainer';
import AnimatedCheckbox from '~/components/molecule/AnimatedCheckbox';
import { Label } from '~/components/atom/Typography';
import Icon from '~/components/atom/Icon';
import LocationSettingsPopup from '../LocationSettingsPopup';
import Divider from '~/components/atom/Divider';
import hasValue from '~/util/hasValue';
import LfaSettingsStatus from '../../../LfaSettingsStatus';
import type { TouchedFields } from '../../../../hooks/useSettingsValidation';

const text = {
  preferred: '1ste voorkeur',
};

export const locationLabelMap: Record<Lfa_Conversion_Location_Type, string> = {
  Office: 'Kantoor',
  OnLocation: 'Op locatie',
  Phone: 'Telefoon',
};

type Props = {
  option: LfaServiceDetailsFieldsFragment;
  location: LfaServiceLocationFieldsFragment;
  isLocationSelected: boolean;
  isLast: boolean;
  currentLocation?: LfaServiceLocationFieldsFragment;
  dispatch: React.Dispatch<UpdateSettingsAction>;
  setFieldTouched: (field: keyof TouchedFields) => void;
};

const LocationItem: React.FC<Props> = ({
  option,
  location,
  isLocationSelected,
  isLast,
  currentLocation,
  dispatch,
  setFieldTouched,
}) => {
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const openerRef = useRef<HTMLDivElement>(null);
  const note = currentLocation?.note?.trim();
  const isMarkedAsPreffered = currentLocation && currentLocation.weight > 0;

  return (
    <JustificationContainer
      width="100%"
      justification="space-between"
      direction="column"
    >
      <JustificationContainer
        width="100%"
        align="center"
        gap="s"
        justification="space-between"
      >
        <AnimatedCheckbox
          selectable={true}
          label={
            <JustificationContainer align="center" gap="s">
              <Label size="base" margin={['xxxs', null, null, null]}>
                {locationLabelMap[location.type]}{' '}
                {hasValue(note) && `(${note})`}
              </Label>
              {isMarkedAsPreffered && (
                <LfaSettingsStatus
                  label={text.preferred}
                  backgroundColor={{ group: 'success', variant: 'translucent' }}
                  textColor={{ group: 'success' }}
                />
              )}
            </JustificationContainer>
          }
          value={isLocationSelected}
          onChange={() => {
            setFieldTouched('locations');
            dispatch({
              type: 'TOGGLE_LOCATIONS',
              payload: {
                serviceId: option.id,
                location: location.type,
              },
            });
          }}
        />
        {isLocationSelected && (
          <Icon
            ref={openerRef}
            name="gear"
            onClick={() => setShowSettings(!showSettings)}
          />
        )}
      </JustificationContainer>
      {showSettings && (
        <LocationSettingsPopup
          openerRef={openerRef}
          option={option}
          location={location}
          currentLocation={currentLocation}
          dispatch={dispatch}
          onClose={() => setShowSettings(false)}
        />
      )}

      {!isLast && <Divider margin={['xxs', null, null, null]} />}
    </JustificationContainer>
  );
};

export default LocationItem;
